import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Loader2, X, Hourglass, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const HIDE_KEY = "tenx_followers_nudge_hidden";

export function PendingFollowersBanner({ companyId, onAdded }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [hidden, setHidden] = useState(() => sessionStorage.getItem(HIDE_KEY) === "1");

  const load = () => api.get(`/companies/${companyId}/pending-followers`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { if (companyId) load(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (r) => {
    setBusy(r.email);
    try {
      const { data } = await api.post(`/companies/${companyId}/investors/quick-add`, { email: r.email, send_latest_update: true });
      toast.success(data.update_sent ? `${r.name} added - "${data.update_sent.title}" is on its way` : `${r.name} added as an investor`);
      setRows((list) => list.filter((x) => x.email !== r.email));
      onAdded?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add investor"); }
    finally { setBusy(null); }
  };

  if (hidden || rows.length === 0) return null;
  return (
    <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50/70 p-4" data-testid="pending-followers-banner">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-800"><Hourglass className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground" data-testid="pending-followers-title">
            {rows.length === 1 ? "An investor joined tenx to follow you and is still waiting" : `${rows.length} investors joined tenx to follow you and are still waiting`}
          </p>
          <p className="text-xs text-muted-foreground">They tapped “keep me posted” on your deck and created an account. One tap adds them and sends your latest update.</p>
          <ul className="mt-3 space-y-2">
            {rows.map((r) => (
              <li key={r.email} className="flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2" data-testid={`pending-follower-${r.notification_id}`}>
                <span className="text-sm font-semibold text-foreground">{r.name}</span>
                <span className="text-xs text-muted-foreground">{r.email} · waiting {r.days_waiting} day{r.days_waiting === 1 ? "" : "s"}</span>
                {r.digest_opens > 0 ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700" title={r.last_digest_opened_at ? `Last opened ${new Date(r.last_digest_opened_at).toLocaleString()}` : ""} data-testid={`pending-follower-opens-${r.notification_id}`}>
                    <Eye className="h-3 w-3" /> opened your digest ×{r.digest_opens} · warming up
                  </span>
                ) : r.digests_sent > 0 ? (
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground" data-testid={`pending-follower-opens-${r.notification_id}`}>digest sent, not opened yet</span>
                ) : null}
                <Button size="sm" className="ml-auto h-7 gap-1.5 text-xs" disabled={busy === r.email} onClick={() => add(r)} data-testid={`pending-follower-add-${r.notification_id}`}>
                  {busy === r.email ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />} Add + send update
                </Button>
              </li>
            ))}
          </ul>
        </div>
        <button type="button" onClick={() => { sessionStorage.setItem(HIDE_KEY, "1"); setHidden(true); }} className="rounded p-1 text-muted-foreground hover:bg-amber-100 hover:text-foreground" aria-label="Hide for now" data-testid="pending-followers-dismiss">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
